/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { defaultImg } from "../utils/icons";

import {
  phylaByKingdom,
  classesByPhylum,
  ordersByClass,
  familiesByOrder,
  generaByFamily,
} from "../utils/taxonomy";

import { phylumImages } from "./PhylumPage";
import { classImages } from "./ClassPage";
import { orderImages } from "./OrderPage";
import { familyImages } from "./FamilyPage";

import { getStorage, ref, listAll } from "firebase/storage";

function flattenUnique(values) {
  return [...new Set(values.flat().filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b)),
  );
}

function normalizeName(value) {
  if (typeof value === "string") return value;
  if (value?.name) return value.name;
  if (value?.genus) return value.genus;
  if (value?.family) return value.family;
  if (value?.order) return value.order;
  if (value?.class) return value.class;
  if (value?.phylum) return value.phylum;
  if (value?.kingdom) return value.kingdom;
  return String(value);
}

function filenameForTaxon(name) {
  return `${name}.png`;
}

function TaxonCard({ item }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex gap-4 items-center">
      <img
        src={defaultImg}
        alt="default fallback"
        className="w-14 h-14 object-contain rounded-xl border border-slate-200 bg-white p-2"
      />

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">
          {item.level}
        </div>

        <div className="text-sm text-slate-800">{item.name}</div>

        <div className="text-xs text-slate-500">{item.expected}</div>
      </div>
    </div>
  );
}

export default function AdminDefaultTaxaImagesPage() {
  const [missingTaxa, setMissingTaxa] = useState([]);

  useEffect(() => {
    async function runAudit() {
      const storage = getStorage();
      const iconsRef = ref(storage, "icons");

      const result = await listAll(iconsRef);

      const existingFiles = new Set(result.items.map((item) => item.name));

      const phyla = flattenUnique(Object.values(phylaByKingdom || {}));
      const classes = flattenUnique(Object.values(classesByPhylum || {}));
      const orders = flattenUnique(Object.values(ordersByClass || {}));
      const families = flattenUnique(Object.values(familiesByOrder || {}));
      const genera = flattenUnique(Object.values(generaByFamily || {}));

      const missing = [];

      /* ---------- GENUS CHECK (Firebase file) ---------- */

      for (const genus of genera) {
        const name = normalizeName(genus);
        const filename = filenameForTaxon(name);

        if (!existingFiles.has(filename)) {
          missing.push({
            level: "Genus",
            name,
            expected: `missing file ${filename}`,
          });
        }
      }

      /* ---------- FAMILY CHECK (map) ---------- */

      for (const family of families) {
        const name = normalizeName(family);

        if (!familyImages[name]) {
          missing.push({
            level: "Family",
            name,
            expected: "no representative genus defined",
          });
        }
      }

      /* ---------- ORDER CHECK ---------- */

      for (const order of orders) {
        const name = normalizeName(order);

        if (!orderImages[name]) {
          missing.push({
            level: "Order",
            name,
            expected: "no representative genus defined",
          });
        }
      }

      /* ---------- CLASS CHECK ---------- */

      for (const cls of classes) {
        const name = normalizeName(cls);

        if (!classImages[name]) {
          missing.push({
            level: "Class",
            name,
            expected: "no representative genus defined",
          });
        }
      }

      /* ---------- PHYLUM CHECK ---------- */

      for (const phylum of phyla) {
        const name = normalizeName(phylum);

        if (!phylumImages[name]) {
          missing.push({
            level: "Phylum",
            name,
            expected: "no representative genus defined",
          });
        }
      }

      setMissingTaxa(missing);
    }

    runAudit();
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f6f8] px-6 py-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <div>
          <Link
            to="/"
            className="bg-[#1f3d2b] text-white px-6 py-2 rounded-xl shadow-sm hover:shadow-md transition font-medium"
          >
            ← Home
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-10 border border-slate-200 space-y-4">
          <h1
            className="text-4xl text-center text-slate-800"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Taxa Using Default Image
          </h1>

          <div className="flex justify-center">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3 text-sm text-slate-700">
              Total taxa missing icons:{" "}
              <span className="font-semibold">{missingTaxa.length}</span>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {missingTaxa.map((item) => (
            <TaxonCard key={`${item.level}-${item.name}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}
